import React from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import DataTable from 'src/components/ui/data-table';
import { Plus, IdCard } from 'lucide-react';
import { useAllPersonas } from 'src/services/api/queries/personaQueries';
import { formatDate } from 'src/lib/utils';

const getColumns = () => [
  {
    key: 'title',
    header: 'Job Title',
    width: '70%',
    render: (value, row) => (
      <div>
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-semibold text-foreground-strong leading-tight">
            {value}
          </div>
          {row.career_level && (
            <span className="text-[11px] font-medium text-muted-foreground border border-border rounded-md px-1.5 py-0.5 leading-none whitespace-nowrap">
              {row.career_level}
            </span>
          )}
        </div>
        {row.persona_description && (
          <div className="text-[12px] text-muted-foreground font-[400] mt-0.5 leading-snug line-clamp-1">
            {row.persona_description}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'created_at',
    header: 'Created',
    width: '30%',
    render: (value) => (
      <span
        className="text-[12.5px] text-muted-foreground font-[450]"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatDate(value)}
      </span>
    ),
  },
];

const DEFAULT_PAGE_SIZE = 10;

function ListPersonas() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const columns = getColumns();

  // Get current page and page size from URL
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  // Use React Query hook
  const { data: response, isLoading } = useAllPersonas(
    currentPage,
    currentPageSize
  );

  // Initialize URL params if not present
  React.useEffect(() => {
    if (!searchParams.has('pageSize')) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set('pageSize', DEFAULT_PAGE_SIZE.toString());
      setSearchParams(updatedParams);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  const totalItems = response?.data?.pagination?.total || 0;
  const totalPages = response?.data?.pagination?.total_pages || 0;
  const pagedData = response?.data?.personas || [];

  const emptyState = (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
        <div
          className="flex items-center justify-center mb-5 rounded-full bg-border"
          style={{ width: 72, height: 72 }}
        >
          <IdCard
            className="text-muted-foreground"
            style={{ width: 32, height: 32, strokeWidth: 1.5 }}
          />
        </div>
        <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
          No personas found
        </p>
        <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs">
          Create a persona to define role requirements for candidate matching.
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Job Personas"
        actions={
          <Button
            onClick={() => navigate('/persona/create')}
            variant="outline"
            size="headerSm"
            className="gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
            Create Persona
          </Button>
        }
      />
      <PageContainer>
        <DataTable
          data={pagedData}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          className="border-border shadow-[0_1px_3px_rgba(0,0,0,.04)]"
          hover
          stickyHeader
          tableLayout="fixed"
          onRowClick={(row) => navigate(`/persona/edit/${row.id}`)}
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyState={emptyState}
        />
      </PageContainer>
    </div>
  );
}

export default ListPersonas;
